import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { exchangeCodeForTokens } from '../utils/cognito';
import './Auth.css';

/**
 * Landing page for the Cognito social-login redirect. Cognito sends the browser
 * here with a `?code=…`; we exchange it for tokens, seed the Cognito session,
 * warm up the backend session, then go to the dashboard.
 */
const AuthCallback: React.FC = () => {
  const [error, setError] = useState('');
  const { completeSocialLogin } = useAuth();
  const navigate = useNavigate();
  // Guard against React 18 StrictMode double-invoke — the code is single-use.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error_description') || params.get('error');

    const run = async () => {
      if (oauthError) {
        setError(oauthError);
        return;
      }
      if (!code) {
        setError('Missing authorization code.');
        return;
      }
      try {
        await exchangeCodeForTokens(code, state);
        await completeSocialLogin();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Sign-in failed.');
      }
    };
    run();
  }, [completeSocialLogin, navigate]);

  return (
    <main id="main-content" className="auth-container">
      <div className="auth-card">
        {error ? (
          <>
            <h2>Sign-in failed</h2>
            <div className="error-message" role="alert">{error}</div>
            <div className="auth-links">
              <button type="button" className="auth-button" onClick={() => navigate('/login', { replace: true })}>
                Back to login
              </button>
            </div>
          </>
        ) : (
          <h2>Signing you in…</h2>
        )}
      </div>
    </main>
  );
};

export default AuthCallback;
